import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Download, ChevronDown, ChevronUp, Utensils, Sparkles, Camera, Activity, Moon, Flower2, MessageCircle, Calendar, Shield, Smartphone } from 'lucide-react';

export default function GuidePage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';
  const [openSection, setOpenSection] = useState(0);

  const toggle = (i) => setOpenSection(openSection === i ? -1 : i);

  const handlePDF = () => {
    window.print();
  };

  const sections = de ? [
    {
      icon: Smartphone, title: 'Erste Schritte',
      steps: [
        { title: 'Konto erstellen', desc: 'Registriere dich kostenlos mit E-Mail oder Google. Du kannst sofort starten – keine Kreditkarte nötig.' },
        { title: 'Onboarding ausfüllen (2 Min)', desc: 'Beantworte 5 kurze Abschnitte: Ziele & Körper, Hormone & Zyklus, Medikamente, Lebensstil, Ernährung. Daraus berechnet belosophie deinen persönlichen GLÖW Score.' },
        { title: 'Dashboard entdecken', desc: 'Nach dem Onboarding landest du auf deinem Dashboard. Hier siehst du deinen Glow Score, deine nächsten Schritte und kannst alle Module nutzen.' },
      ]
    },
    {
      icon: Utensils, title: 'Ernährungs-Tracking',
      steps: [
        { title: 'Mahlzeit fotografieren', desc: 'Gehe zu "Tracking" und tippe auf das Kamera-Symbol neben einer Mahlzeit (Frühstück, Mittagessen, Abendessen oder Snack). Die KI analysiert automatisch Kalorien, Protein, Kohlenhydrate und Fett.' },
        { title: 'Triple Score verstehen', desc: 'Jede Mahlzeit wird dreifach bewertet: Hashimoto-Score (Selen, Protein), Zyklus-Score (Magnesium, Blutzucker) und Glow-Score (Antioxidantien, Hautfreundlichkeit).' },
        { title: 'Stimmung & Energie loggen', desc: 'Wähle deine Stimmung (5 Smileys) und Energie (Schieberegler). Diese Daten helfen der KI, Muster zu erkennen.' },
        { title: 'Gewohnheiten abhaken', desc: 'Tracke tägliche Gewohnheiten: 8 Gläser Wasser, Gemüse, 10 Min Spaziergang, achtsames Essen, Protein bei jeder Mahlzeit.' },
      ]
    },
    {
      icon: Sparkles, title: 'Glow Score & Selfie-Analyse',
      steps: [
        { title: 'Tägliches Selfie machen', desc: 'Auf dem Dashboard tippe auf "Selfie-Check". Die KI (OpenAI Vision) analysiert: Hautglanz, Hydration, Pigmentierung, Augenringe und Gleichmässigkeit.' },
        { title: 'Glow Score verfolgen', desc: 'Dein Glow Score (0-100) setzt sich zusammen aus: Haut (30%), Taille (20%), Energie (20%), Schlaf (20%), Training (10%). Je höher, desto besser.' },
        { title: 'Körpermasse tracken', desc: 'Im Glow-Modul kannst du wöchentlich Taille, Oberarm und Hüfte messen. Die Trends zeigen dir Fortschritte jenseits der Waage.' },
      ]
    },
    {
      icon: MessageCircle, title: 'KI-Ernährungscoach',
      steps: [
        { title: 'Chat starten', desc: 'Gehe zu "Chat". Der KI-Coach hat 4 Kompetenzen: Ernährungsmedizin, Sportmedizin, Fitness-Coaching und Psychologie.' },
        { title: 'Fotos senden', desc: 'Tippe auf das 📎-Symbol um Fotos zu senden. Der Coach analysiert Mahlzeiten, Zutatenlisten oder Einkäufe.' },
        { title: 'Spracheingabe nutzen', desc: 'Tippe auf das 🎤-Symbol und sprich deine Frage. Die Sprache wird automatisch in Text umgewandelt (Whisper KI).' },
        { title: 'Vorlesen lassen', desc: 'Aktiviere den Lautsprecher (oben rechts) oder tippe auf das 🔊-Symbol bei einer Antwort. Der Coach liest dir die Antwort vor.' },
      ]
    },
    {
      icon: Activity, title: 'Hashimoto-Modul',
      steps: [
        { title: 'Laborwerte eintragen', desc: 'Trage TSH, fT3 und fT4 ein. Die App zeigt Trend-Grafiken über Wochen und warnt bei kritischen Werten (z.B. TSH über 4.0).' },
        { title: 'Selen & Omega-3 tracken', desc: 'Bewerte täglich deinen Selen- und Omega-3-Score (1-10). Bei niedrigem Selen empfiehlt die App 2-3 Paranüsse pro Tag.' },
        { title: 'Symptom-Heatmap nutzen', desc: 'Nach 7+ Einträgen erscheint eine farbcodierte Heatmap: Grün (wenig Symptome) bis Rot (stark). So erkennst du Muster.' },
      ]
    },
    {
      icon: Moon, title: 'Menopause-Modul',
      steps: [
        { title: 'Hitzewallungen zählen', desc: 'Tippe die Anzahl (0 bis 6+). Gleiches für Nachtschweiss. Plus: Stimmung, Schlaf, Brain Fog und Gelenkschmerzen (0-10 Slider).' },
        { title: 'Trigger markieren', desc: 'Wähle Auslöser: Zucker, Alkohol, Kaffee, Stress, Schlafmangel, Sport etc. Die App analysiert Korrelationen.' },
        { title: 'Trigger-Analyse lesen', desc: 'Nach 5+ Einträgen zeigt die App: "Zucker → Hitzewallungen +30%" oder "Sport → Schlaf +20%". So lernst du, was dir hilft.' },
      ]
    },
    {
      icon: Flower2, title: 'Zyklus-Modul',
      steps: [
        { title: 'Zyklustag eintragen', desc: 'Trage deinen aktuellen Zyklustag und Phase (Menstruation, Follikel, Eisprung, Luteal) ein.' },
        { title: 'Temperatur loggen', desc: 'Optional: Basaltemperatur morgens messen und eintragen. Hilft bei der Phasen-Erkennung.' },
        { title: 'Phasenbasierte Tipps', desc: 'Die KI passt Ernährungstipps an deine Phase an: z.B. "Lutealphase: Magnesium statt Zucker gegen Heisshunger".' },
      ]
    },
    {
      icon: Calendar, title: 'Video-Calls buchen (Premium)',
      steps: [
        { title: 'Termin wählen', desc: 'Gehe zu "Call buchen" im Dashboard. Wähle ein Datum (Mo-Fr) und eine verfügbare Uhrzeit.' },
        { title: 'Wochenregel beachten', desc: 'Du kannst maximal 1 Call pro Woche buchen. Wochen mit Termin sind im Kalender farblich markiert.' },
        { title: 'Vorausbuchen', desc: 'Du kannst beliebig viele Wochen im Voraus buchen – für bessere Planbarkeit.' },
        { title: 'Bestätigung', desc: 'Nach der Buchung erhältst du eine E-Mail-Bestätigung. Den Termin kannst du jederzeit stornieren.' },
      ]
    },
    {
      icon: Shield, title: 'Medikamenten-Timer',
      steps: [
        { title: 'Automatische Erkennung', desc: 'Basierend auf deinem Onboarding zeigt das Dashboard Medikamenten-Erinnerungen: z.B. "Levothyroxin 07:00 – Nüchtern, 60 Min vor Frühstück".' },
        { title: 'Wechselwirkungen beachten', desc: 'Die App warnt bei Interaktionen: Levothyroxin + Soja/Kalzium vermeiden. Pantoprazol → Magnesium-Smoothie um 16 Uhr.' },
      ]
    },
  ] : [
    {
      icon: Smartphone, title: 'Getting Started',
      steps: [
        { title: 'Create account', desc: 'Register free with email or Google. Start immediately – no credit card needed.' },
        { title: 'Complete onboarding (2 min)', desc: 'Answer 5 sections: Goals, Hormones, Medications, Lifestyle, Nutrition. belosophie calculates your personal GLÖW Score.' },
        { title: 'Explore dashboard', desc: 'After onboarding you land on your dashboard with Glow Score, next steps and all modules.' },
      ]
    },
    {
      icon: Utensils, title: 'Nutrition Tracking',
      steps: [
        { title: 'Photograph meals', desc: 'Go to "Tracking" and tap the camera icon. AI analyzes calories, protein, carbs and fat automatically.' },
        { title: 'Understand Triple Score', desc: 'Each meal gets three scores: Hashimoto (selenium, protein), Cycle (magnesium, blood sugar), Glow (antioxidants).' },
        { title: 'Log mood & energy', desc: 'Choose mood (5 smileys) and energy (slider). This data helps AI recognize patterns.' },
        { title: 'Check habits', desc: 'Track daily habits: 8 glasses water, vegetables, 10 min walk, mindful eating, protein at every meal.' },
      ]
    },
    { icon: Sparkles, title: 'Glow Score & Selfie', steps: [
      { title: 'Daily selfie', desc: 'Tap "Selfie Check" on dashboard. AI analyzes glow, hydration, pigmentation, dark circles.' },
      { title: 'Track Glow Score', desc: 'Score 0-100: Skin 30%, Waist 20%, Energy 20%, Sleep 20%, Training 10%.' },
      { title: 'Body measurements', desc: 'Track waist, upper arm, hips weekly in the Glow module.' },
    ]},
    { icon: MessageCircle, title: 'AI Coach', steps: [
      { title: 'Start chat', desc: 'Go to "Chat". 4 competencies: Nutrition, Sports Medicine, Fitness, Psychology.' },
      { title: 'Send photos', desc: 'Tap 📎 to send meal photos for analysis.' },
      { title: 'Voice input', desc: 'Tap 🎤 and speak. Whisper AI transcribes automatically.' },
      { title: 'Listen', desc: 'Enable speaker (top right) or tap 🔊 on any response.' },
    ]},
    { icon: Calendar, title: 'Video Calls (Premium)', steps: [
      { title: 'Book appointment', desc: 'Go to "Book Call". Choose date (Mon-Fri) and time. Max 1 per week. Book weeks ahead.' },
      { title: 'Confirmation', desc: 'You receive email confirmation. Cancel anytime.' },
    ]},
  ];

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl print:max-w-none print:p-8" data-testid="guide-page">
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight">{de ? 'Benutzeranleitung' : 'User Guide'}</h1>
            <p className="font-body text-sm text-foreground/50">{de ? 'So holst du das Beste aus belosophie' : 'Get the most out of belosophie'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/belosophie-tutorial-user.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border/60 font-body text-sm font-medium text-foreground/60 hover:border-terracotta/50 hover:text-terracotta transition-colors" data-testid="guide-user-pdf">
            <Download size={14} /> {de ? 'User PDF' : 'User PDF'}
          </a>
          {user?.role === 'admin' && (
            <a href="/belosophie-tutorial-admin.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-terracotta/30 font-body text-sm font-medium text-terracotta hover:bg-terracotta/5 transition-colors" data-testid="guide-admin-pdf">
              <Download size={14} /> Admin PDF
            </a>
          )}
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="font-display text-4xl font-medium"><span className="text-terracotta">bel</span>osophie</h1>
        <p className="font-body text-lg text-foreground/50 mt-2">{de ? 'Benutzeranleitung' : 'User Guide'}</p>
      </div>

      <div className="space-y-3">
        {sections.map((section, i) => {
          const Icon = section.icon;
          const isOpen = openSection === i;
          return (
            <Card key={i} className="bg-white border border-border/60 rounded-2xl overflow-hidden print:shadow-none print:border print:break-inside-avoid" data-testid={`guide-section-${i}`}>
              <button onClick={() => toggle(i)} className="w-full flex items-center gap-4 p-5 text-left print:pointer-events-none">
                <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-terracotta" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-lg font-medium">{section.title}</h2>
                    <Badge className="bg-warmgray/50 text-foreground/40 border-0 text-[10px] rounded-full">{section.steps.length} {de ? 'Schritte' : 'steps'}</Badge>
                  </div>
                </div>
                <div className="print:hidden">
                  {isOpen ? <ChevronUp size={18} className="text-foreground/30" /> : <ChevronDown size={18} className="text-foreground/30" />}
                </div>
              </button>
              {(isOpen || false) && (
                <div className="px-5 pb-5 space-y-3 print:block">
                  {section.steps.map((step, j) => (
                    <div key={j} className="flex gap-3 p-3 rounded-xl bg-warmgray/30" data-testid={`guide-step-${i}-${j}`}>
                      <div className="w-7 h-7 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="font-body text-xs font-bold text-terracotta">{j + 1}</span>
                      </div>
                      <div>
                        <h3 className="font-display text-sm font-medium">{step.title}</h3>
                        <p className="font-body text-xs text-foreground/60 mt-1 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Always show in print */}
              <div className="hidden print:block px-5 pb-5 space-y-3">
                {section.steps.map((step, j) => (
                  <div key={j} className="flex gap-3 p-3 rounded-xl bg-warmgray/30">
                    <div className="w-7 h-7 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="font-body text-xs font-bold text-terracotta">{j + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-display text-sm font-medium">{step.title}</h3>
                      <p className="font-body text-xs text-foreground/60 mt-1 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
